import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, ListGroup, Alert, Badge, ProgressBar } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import '../../styles/VideoConverter.css';

const VideoConverter = () => {
  const [videoFiles, setVideoFiles] = useState([]);
  const [outputFormat, setOutputFormat] = useState('mp4');
  const [quality, setQuality] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [converting, setConverting] = useState(false);
  const [ffmpeg, setFFmpeg] = useState(null);
  const [ffmpegLoaded, setFFmpegLoaded] = useState(false);
  const fileInputRef = useRef(null);

  const supportedFormats = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv'];
  const qualityOptions = [
    { value: 'low', label: 'Low (480p)', description: 'Smaller file size, reduced quality', crf: '28', scale: '854:480' },
    { value: 'medium', label: 'Medium (720p)', description: 'Balanced quality and file size', crf: '23', scale: '1280:720' },
    { value: 'high', label: 'High (1080p)', description: 'High quality, larger file size', crf: '18', scale: '1920:1080' },
  ];

  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        const ffmpegInstance = new FFmpeg();
        
        ffmpegInstance.on('log', ({ message }) => {
          console.log('FFmpeg log:', message);
        });
        
        ffmpegInstance.on('progress', ({ progress }) => {
          setProgress(Math.round(progress * 100));
        });
        
        // Load FFmpeg - using CDN for WASM files instead of local path
        await ffmpegInstance.load({
          coreURL: 'https://unpkg.com/@ffmpeg/core@0.12.2/dist/ffmpeg-core.js',
          wasmURL: 'https://unpkg.com/@ffmpeg/core@0.12.2/dist/ffmpeg-core.wasm'
        });
        
        setFFmpeg(ffmpegInstance);
        setFFmpegLoaded(true);
        console.log('FFmpeg loaded successfully');
      } catch (error) {
        console.error('Error loading FFmpeg:', error);
        setError('Failed to load video conversion library. Please try again later or check your internet connection.');
      }
    };
    
    loadFFmpeg();
    
    return () => {
      if (ffmpeg) {
        ffmpeg.terminate();
      }
    };
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const videoFilesToAdd = files.filter(file => file.type.startsWith('video/'));
    
    if (videoFiles.length + videoFilesToAdd.length > 5) {
      setError('You can only upload up to 5 video files.');
      return;
    }

    setError('');
    setSuccess(false);
    
    const newVideoFiles = videoFilesToAdd.map(file => {
      let format = file.type.split('/')[1] || file.name.split('.').pop().toLowerCase();
      
      return {
        file,
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2),
        originalFormat: format,
        convertedFile: null
      };
    });

    setVideoFiles([...videoFiles, ...newVideoFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    const updatedFiles = [...videoFiles];
    
    if (updatedFiles[index].convertedFile && updatedFiles[index].convertedFile.url) {
      URL.revokeObjectURL(updatedFiles[index].convertedFile.url);
    }
    
    updatedFiles.splice(index, 1);
    setVideoFiles(updatedFiles);
    setSuccess(false);
  };

  const convertVideo = async (videoFile, index) => {
    try {
      if (!ffmpeg || !ffmpegLoaded) {
        throw new Error('FFmpeg not loaded');
      }
      
      const inputFileName = `input_${index}.${videoFile.originalFormat}`;
      const outputFileName = `output_${index}.${outputFormat}`;
      
      await ffmpeg.writeFile(inputFileName, await fetchFile(videoFile.file));
      
      const qualitySetting = qualityOptions.find(option => option.value === quality);
      
      let ffmpegArgs = [];
      
      ffmpegArgs.push('-i', inputFileName);
      
      if (outputFormat === 'mp4' || outputFormat === 'mkv') {
        ffmpegArgs.push('-c:v', 'libx264', '-crf', qualitySetting.crf);
      } else if (outputFormat === 'webm') {
        ffmpegArgs.push('-c:v', 'libvpx', '-crf', qualitySetting.crf);
      } else {
        ffmpegArgs.push('-c:v', 'libx264', '-crf', qualitySetting.crf);
      }
      
      ffmpegArgs.push('-vf', `scale=${qualitySetting.scale}`);
      ffmpegArgs.push('-c:a', 'aac', '-b:a', '128k');
      ffmpegArgs.push('-f', outputFormat, outputFileName);
      
      await ffmpeg.exec(ffmpegArgs);
      
      const data = await ffmpeg.readFile(outputFileName);
      
      const blob = new Blob([data.buffer], { 
        type: `video/${outputFormat}` 
      });
      
      const url = URL.createObjectURL(blob);
      
      return {
        blob,
        url,
        size: blob.size,
        name: videoFile.name.substring(0, videoFile.name.lastIndexOf('.')) + '.' + outputFormat
      };
    } catch (error) {
      console.error('Error converting video:', error);
      throw error;
    }
  };

  const convertVideos = async () => {
    if (videoFiles.length === 0) {
      setError('Please add at least one video file.');
      return;
    }
    
    if (!ffmpegLoaded) {
      setError('Video conversion library is still loading. Please wait a moment and try again.');
      return;
    }
    
    setLoading(true);
    setConverting(true);
    setError('');
    setSuccess(false);
    setProgress(0);

    try {
      const updatedFiles = [...videoFiles];
      const totalFiles = videoFiles.length;
      
      for (let i = 0; i < totalFiles; i++) {
        if (updatedFiles[i].originalFormat === outputFormat) {
          updatedFiles[i].convertedFile = {
            url: URL.createObjectURL(updatedFiles[i].file),
            size: updatedFiles[i].file.size,
            name: updatedFiles[i].name,
            skipped: true
          };
          setProgress(((i + 1) / totalFiles) * 100);
          continue;
        }
        
        try {
          const convertedFile = await convertVideo(updatedFiles[i], i);
          updatedFiles[i].convertedFile = convertedFile;
        } catch (error) {
          console.error(`Error converting file ${i}:`, error);
          setError(`Failed to convert ${updatedFiles[i].name}. Please try again.`);
        }
      }
      
      setVideoFiles(updatedFiles);
      setSuccess(true);
    } catch (err) {
      console.error('Error in conversion process:', err);
      setError('Failed to convert videos. Please try again with valid video files.');
    } finally {
      setLoading(false);
      setConverting(false);
      setProgress(100);
    }
  };

  const handleDownload = (index) => {
    const file = videoFiles[index];
    
    if (!file.convertedFile) {
      setError(`The file ${file.name} has not been converted yet.`);
      return;
    }
    
    const link = document.createElement('a');
    link.href = file.convertedFile.url;
    link.download = file.convertedFile.name || `converted-video-${index}.${outputFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadAll = () => {
    videoFiles.forEach((file, index) => {
      if (file.convertedFile) {
        handleDownload(index);
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Container className="page-container video-converter-container">
      <Helmet>
        <title>Video Converter - Convert Video Formats Online Free | MicroGenie</title>
        <meta name="description" content="Convert videos between MP4, MOV, AVI, MKV, and more formats online for free. No software installation needed - fast, easy, and secure video conversion." />
        <meta name="keywords" content="video converter, convert video online, mp4 converter, mov to mp4, video format converter, free video converter, online video converter" />
        <meta property="og:title" content="Video Converter - Convert Video Formats Online Free | MicroGenie" />
        <meta property="og:description" content="Convert videos between MP4, MOV, AVI, MKV, and more formats online for free. No software installation needed." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Video Converter - Convert Video Formats Online Free | MicroGenie" />
        <meta name="twitter:description" content="Convert videos between MP4, MOV, AVI, MKV, and more formats online for free. No software installation needed - fast, easy, and secure video conversion." />
      </Helmet>

      <Row className="justify-content-center mb-5">
        <Col lg={10}>
          <div className="text-center">
            <h1 className="section-title">Video Converter</h1>
            <p className="lead mb-4">
              Convert your videos to different formats with ease. Support for MP4, MOV, AVI, MKV, WebM, and more.
              Upload up to 5 videos, select your desired output format, and convert with one click.
            </p>
          </div>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="video-converter-card">
            <Card.Body>
              <div className="file-upload-section">
                <div className="upload-area text-center p-5">
                  <i className="la la-video mb-3" style={{ fontSize: '48px', color: '#8a4fff' }} aria-hidden="true"></i>
                  <h3 className="mb-3">Upload Videos</h3>
                  <p className="text-muted mb-2">Upload up to 5 video files (max 100MB each)</p>
                  <p className="supported-formats mb-4">
                    Supported formats: 
                    {supportedFormats.map((format, index) => (
                      <Badge key={index} bg="light" text="dark" className="format-badge ms-2">{format.toUpperCase()}</Badge>
                    ))}
                  </p>
                  
                  <div className="d-grid gap-2">
                    <Form.Group controlId="formFileMultiple">
                      <Form.Control 
                        type="file" 
                        accept="video/*" 
                        multiple 
                        onChange={handleFileChange} 
                        ref={fileInputRef}
                        className="d-none"
                      />
                      <Button 
                        variant="primary" 
                        className="upload-btn" 
                        onClick={() => fileInputRef.current.click()}
                      >
                        <i className="la la-upload me-2" aria-hidden="true"></i> Choose Video Files
                      </Button>
                    </Form.Group>
                  </div>
                </div>
              </div>

              {!ffmpegLoaded && (
                <Alert variant="info" className="mt-4">
                  <i className="la la-spinner la-spin me-2" aria-hidden="true"></i> Loading video conversion library... This may take a moment.
                </Alert>
              )}

              {error && <Alert variant="danger" className="mt-4">{error}</Alert>}
              {success && <Alert variant="success" className="mt-4">Videos have been converted successfully!</Alert>}

              {videoFiles.length > 0 && (
                <div className="conversion-settings mt-5">
                  <h4 className="mb-4">Conversion Settings</h4>
                  
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Output Format</strong></Form.Label>
                        <Form.Select 
                          value={outputFormat}
                          onChange={(e) => setOutputFormat(e.target.value)}
                        >
                          {supportedFormats.map((format) => (
                            <option key={format} value={format}>{format.toUpperCase()}</option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Select the format you want to convert your videos to
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Quality</strong></Form.Label>
                        <Form.Select 
                          value={quality}
                          onChange={(e) => setQuality(e.target.value)}
                        >
                          {qualityOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {qualityOptions.find(option => option.value === quality)?.description}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              {videoFiles.length > 0 && (
                <div className="video-list-section mt-4">
                  <h4 className="mb-4">Selected Videos ({videoFiles.length}/5)</h4>
                  <ListGroup className="video-files-list">
                    {videoFiles.map((file, index) => (
                      <ListGroup.Item key={index} className="video-file-item">
                        <div className="d-flex align-items-center">
                          <div className="me-2 file-number">{index + 1}.</div>
                          <i className="la la-file-video me-3" style={{ fontSize: '24px', color: '#8a4fff' }} aria-hidden="true"></i>
                          <div className="file-info flex-grow-1">
                            <div className="file-name">{file.name}</div>
                            <div className="file-details text-muted small">
                              {file.size} MB • {file.originalFormat.toUpperCase()} 
                              {file.originalFormat !== outputFormat && (
                                <span className="conversion-badge">
                                  <i className="la la-arrow-right mx-1" aria-hidden="true"></i> 
                                  {outputFormat.toUpperCase()}
                                </span>
                              )}
                              {file.convertedFile && (
                                <span className="ms-2 text-success">
                                  <i className="la la-check-circle me-1" aria-hidden="true"></i>
                                  {file.convertedFile.skipped ? 'Ready' : 'Converted'} 
                                  ({formatFileSize(file.convertedFile.size)})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="file-actions">
                            <Button 
                              variant="light" 
                              className="btn-sm text-danger"
                              onClick={() => removeFile(index)}
                            >
                              <i className="la la-times" aria-hidden="true"></i>
                            </Button>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              {videoFiles.length > 0 && (
                <div className="action-buttons mt-4 text-center">
                  {converting ? (
                    <div className="mb-3">
                      <ProgressBar 
                        animated 
                        now={progress} 
                        label={`${Math.round(progress)}%`} 
                        className="conversion-progress" 
                      />
                      <p className="text-muted mt-2">Converting your videos... This may take a few minutes depending on file size.</p>
                    </div>
                  ) : null}
                  
                  {!success ? (
                    <Button 
                      variant="primary" 
                      className="convert-btn" 
                      onClick={convertVideos}
                      disabled={loading || !ffmpegLoaded}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Converting...
                        </>
                      ) : (
                        <>
                          <i className="la la-refresh me-2" aria-hidden="true"></i> Convert Videos
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="download-options">
                      <p className="text-success mb-3">
                        <i className="la la-check-circle me-2" aria-hidden="true"></i>
                        Conversion complete! You can now download your converted videos.
                      </p>
                      <Row>
                        {videoFiles.map((file, index) => (
                          <Col md={6} lg={4} key={index} className="mb-3">
                            {file.convertedFile && (
                              <Button 
                                variant="success" 
                                className="download-btn w-100"
                                onClick={() => handleDownload(index)}
                              >
                                <i className="la la-download me-2" aria-hidden="true"></i>
                                Download {file.convertedFile.name || `${file.name.split('.')[0]}.${outputFormat}`}
                              </Button>
                            )}
                          </Col>
                        ))}
                      </Row>
                      {videoFiles.length > 1 && videoFiles.filter(file => file.convertedFile).length > 1 && (
                        <Button 
                          variant="primary" 
                          className="download-all-btn mt-3"
                          onClick={handleDownloadAll}
                        >
                          <i className="la la-download me-2" aria-hidden="true"></i>
                          Download All Files
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>

          <div className="video-info mt-5">
            <h4>About Our Video Converter Tool</h4>
            <p>
              Our Video Converter Tool allows you to convert videos to different formats quickly and easily. 
              This free online tool works directly in your browser, ensuring that your files remain secure and private.
            </p>
            
            <h5 className="mt-4">How to use the Video Converter Tool:</h5>
            <ol>
              <li>Click the "Choose Video Files" button and select up to 5 video files you want to convert.</li>
              <li>Choose your desired output format from the dropdown menu.</li>
              <li>Select the quality setting that best suits your needs.</li>
              <li>Click the "Convert Videos" button to start the conversion process.</li>
              <li>Once conversion is complete, download your converted video files individually or all at once.</li>
            </ol>
            
            <div className="features-info mt-4">
              <h5>Features:</h5>
              <ul>
                <li><strong>Multiple Format Support:</strong> Convert between popular video formats including MP4, MOV, AVI, MKV, WebM, and more.</li>
                <li><strong>Batch Conversion:</strong> Convert up to 5 videos at once to save time.</li>
                <li><strong>Quality Options:</strong> Choose between different quality settings to balance file size and video quality.</li>
                <li><strong>No Registration Required:</strong> Use our tool without creating an account or providing personal information.</li>
                <li><strong>100% Free:</strong> No hidden fees or premium features - all conversion capabilities are completely free.</li>
              </ul>
            </div>
            
            <div className="security-info mt-4">
              <h5>Privacy & Security:</h5>
              <p>
                Your video files are processed entirely in your browser and are not uploaded to our servers. 
                This ensures complete privacy and security for your media content.
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default VideoConverter;